import {
  FOC_NOTIFICATIONS_TOUR_STEPS,
  type FocNotificationsTourStepId,
  type FocNotificationsTourSurface,
} from './focNotificationsTourSteps';

/**
 * The tour runs outside React, so Notifications hosts register the UI moves
 * they alone can perform.
 */
export type FocNotificationsTourApi = {
  showNotifications: () => void;
  openCompose: () => void;
  closeCompose: () => void;
  setFilter: (filter: 'active' | 'historical' | 'sent') => void;
  openAutomationSettings: () => void;
  closeAutomationSettings: () => void;
  expandFirstNotification: () => void;
  openFirstDeliveryReport: () => void;
  selectStandDownSafety: () => void;
};

let api: Partial<FocNotificationsTourApi> = {};
let pendingStep: FocNotificationsTourStepId | null = null;

export function registerFocNotificationsTourApi(
  partial: Partial<FocNotificationsTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    pendingStep = null;
    prepareFocNotificationsStep(step);
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof FocNotificationsTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocNotificationsTourApi(): Partial<FocNotificationsTourApi> {
  return api;
}

function surfaceFor(id: FocNotificationsTourStepId): FocNotificationsTourSurface {
  return (
    FOC_NOTIFICATIONS_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'list'
  );
}

export function prepareFocNotificationsStep(id: FocNotificationsTourStepId) {
  const host = getFocNotificationsTourApi();
  const surface = surfaceFor(id);

  host.showNotifications?.();

  if (surface === 'compose') {
    host.closeAutomationSettings?.();
    host.openCompose?.();
    if (id === 'us000') {
      host.selectStandDownSafety?.();
    }
    return;
  }

  if (surface === 'automation') {
    host.closeCompose?.();
    host.openAutomationSettings?.();
    return;
  }

  if (surface === 'delivery') {
    host.closeAutomationSettings?.();
    host.closeCompose?.();
    host.setFilter?.('sent');
    host.openFirstDeliveryReport?.();
    return;
  }

  // list
  host.closeAutomationSettings?.();
  host.closeCompose?.();

  switch (id) {
    case 'us004':
    case 'us015':
      host.setFilter?.(id === 'us015' ? 'historical' : 'active');
      return;
    case 'us003':
    case 'us011':
      host.setFilter?.('active');
      host.expandFirstNotification?.();
      return;
    case 'us013':
      host.setFilter?.('active');
      return;
    case 'focFeedback':
      host.setFilter?.('active');
      return;
    default:
      host.setFilter?.('active');
  }
}

export function resetFocNotificationsTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
